import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, ListChecks, Vault, FileText, Calendar,
  BarChart3, ShieldAlert, LogOut, Sun, Moon, Search, Building2, UsersRound, Repeat, Wallet, AlertTriangle
} from "lucide-react";
import { useEffect, useState } from "react";
import GlobalSearch from "@/components/GlobalSearch";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/recurring", label: "Recurring Tasks", icon: Repeat, testid: "nav-recurring" },
  { to: "/calendar", label: "Compliance Calendar", icon: Calendar, testid: "nav-calendar" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/billing", label: "Billing", icon: Wallet, testid: "nav-billing" },
  { to: "/outstanding", label: "All Outstanding", icon: AlertTriangle, testid: "nav-outstanding" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team" },
  { to: "/firm", label: "Firm Settings", icon: Building2, testid: "nav-firm" },
  { to: "/audit", label: "Audit Log", icon: ShieldAlert, testid: "nav-audit" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav_go = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [fy, setFy] = useState("FY 2025-26");
  const [fyList, setFyList] = useState([]);
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/financial-years`, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("tpms_token") || ""}` } }).then(r => r.json()).then(setFyList).catch(() => {}); }, []);

  useEffect(() => {
    function keyHandler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* SIDEBAR */}
      <aside className="w-64 shrink-0 border-r border-border bg-slate-50 dark:bg-zinc-950 flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-blue-600 text-white grid place-items-center font-display font-bold">V</div>
            <div>
              <div className="font-display font-bold text-[13px] leading-none">Vijay Shankar Vyas</div>
              <div className="text-[10px] text-muted-foreground mt-1 tracking-wider uppercase">Tax Practice OS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 pb-2">Workspace</div>
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-zinc-800"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white grid place-items-center font-semibold text-xs">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{user?.role}</div>
            </div>
            <Button data-testid="logout-btn" size="icon" variant="ghost" onClick={async () => { await logout(); nav_go("/login"); }}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-border bg-white/80 dark:bg-zinc-950/80 backdrop-blur sticky top-0 z-30 flex items-center px-6 gap-4">
          <button
            data-testid="global-search-btn"
            onClick={() => setSearchOpen(true)}
            className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-slate-50 dark:bg-zinc-900 text-sm text-muted-foreground hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <Search className="w-4 h-4" />
            <span>Search clients, tasks, documents…</span>
            <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-border bg-white dark:bg-zinc-950 font-mono">⌘K</kbd>
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden md:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border bg-slate-50 dark:bg-zinc-900">
              <Building2 className="w-3.5 h-3.5" />
              <select
                data-testid="fy-selector"
                value={fy}
                onChange={(e) => setFy(e.target.value)}
                className="bg-transparent outline-none font-medium"
              >
                {(fyList.length ? fyList : ["FY 2025-26"]).map(f => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>
            <Button data-testid="theme-toggle" size="icon" variant="ghost" onClick={toggle}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </header>

        <div className="flex-1 p-6 overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
